import PropTypes from "prop-types";
import InputBase from "./InputBase";
import React from "react";
import {DateField, MobileDateTimePicker, TimeField} from "@mui/x-date-pickers";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {de} from 'date-fns/locale';
import GF from "../../../../lib/utils/GF";

/**
 * @property string labelText: Werte=disable/error
 * @property string state: Werte=disable/error
 * @property string helpTextElement: Hilfetext
 * @property string use: Verwendung Werte=table oder leer
 */
class InputDateTimePicker extends InputBase {

  // MUI blendet den Clear-Button (X) standardmässig nur bei Hover/Fokus ein (Desktop).
  // Damit er immer sichtbar ist, wird die opacity fix auf 1 gesetzt.
  static alwaysVisibleClearButtonSx = {
    '& .clearButton': {opacity: 1},
    '@media (pointer: fine)': {
      '& .clearButton': {opacity: 1}
    }
  };

  getValue(value = undefined) {
    let retValString = null;

    if (value === undefined) {
      // Kein Wert übergeben (DateField/TimeField-onChange) -> Rohtext aus dem Input lesen
      let dateTime = this.inputElementRef.current.value;

      if (!dateTime.includes('DD.MM.YYYY')) {
        retValString = dateTime.replace('hh:mm', '');
      }
    } else if (value !== null) {
      retValString = GF.dateTimeToString(value);
    }
    // value === null bedeutet: Feld wurde über den Clear-Button geleert -> retValString bleibt null
    return retValString;
  }

  parseDate(dateStr) {
    return GF.stringToDateTime(dateStr);
  }

  render() {
    const hasError = !!this.state.errorText;
    const errorSlotProps = {
      error: hasError,
      helperText: hasError ? this.state.errorText : null,
    };
    let picker;
    if (this.props.pickerVariant === 'DATE') {
      picker = <DateField
        slotProps={{textField: {...errorSlotProps}}}
        inputRef={this.inputElementRef}
        fullWidth
        clearable
        sx={InputDateTimePicker.alwaysVisibleClearButtonSx}
        onChange={(e) => { const v = this.getValue(); this.onBlur(v); this.fireOnChange(e, v); }}
        ampm={false}
        size={this.props.size}
        defaultValue={this.parseDate(this.props.defaultValue)}
        label={this.props.labelElement}
        disabled={this.props.disabled}/>
    } else if (this.props.pickerVariant === 'TIME') {
      picker = <TimeField
        inputRef={this.inputElementRef}
        slotProps={{textField: {...errorSlotProps}}}
        clearable
        sx={InputDateTimePicker.alwaysVisibleClearButtonSx}
        onChange={(e) => { const v = this.getValue(); this.onBlur(v); this.fireOnChange(e, v); }}
        size={this.props.size}
        fullWidth
        ampm={false}
        label={this.props.labelElement}
        defaultValue={this.parseDate(this.props.defaultValue)}
        disabled={this.props.disabled}/>
    } else {
      picker = <MobileDateTimePicker
        inputRef={this.inputElementRef}
        fullWidth
        onChange={(e) => { const v = this.getValue(e); this.onBlur(v); this.fireOnChange(e, v); }}
        slotProps={{
          textField: {
            size: this.props.size,
            fullWidth: true,
            ...errorSlotProps,
          },
          // Das Feld des MobileDateTimePicker ist ein reines Anzeigefeld (readOnly) -
          // "clearable" am Feld selbst (wie bei DateField/TimeField) hat hier keine Wirkung,
          // da es nur beim editierbaren Field-Rendering ein X-Icon erzeugt. Zum Entfernen
          // des Datums muss stattdessen die "Löschen"-Aktion der Dialog-Actionbar aktiviert
          // werden (standardmässig zeigt die Mobile-Variante nur Abbrechen/OK).
          actionBar: {
            actions: ['clear', 'cancel', 'accept'],
          },
        }}
        ampm={false}
        defaultValue={this.parseDate(this.props.defaultValue)}
        label={this.props.labelElement}
        disabled={this.props.disabled}/>
    }
    return <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      {picker}
    </LocalizationProvider>
  }
}

InputDateTimePicker.propTypes = {
  className: PropTypes.string,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  labelElement: PropTypes.any,
  helpTextElement: PropTypes.node,
  disabled: PropTypes.bool,
  state: PropTypes.oneOf(['none', 'enable', 'disable', 'error', 'readonly']),
  inputLayout: PropTypes.oneOf(['inline', 'outline', '']),
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  pickerVariant: PropTypes.oneOf(['DATE', 'TIME', 'DATETIME']),
  useTime: PropTypes.bool,
  canChooseUseTime: PropTypes.bool
}
export default InputDateTimePicker;
